import {
    AxleDialogShellDragBounds,
    AxleDialogShellDragOffset,
    AxleDialogShellRect,
} from './AxleDialogShell.types';

export const DRAG_ORIGIN: AxleDialogShellDragOffset = { x: 0, y: 0 };

// Elements that own their own pointer interactions, so a press on one is not a drag.
const INTERACTIVE_SELECTOR =
    'button, a, input, textarea, select, [role="button"], [role="tab"], [role="checkbox"]';

export const isDragHandleTarget = (target: EventTarget | null): boolean =>
    target instanceof Element && target.closest(INTERACTIVE_SELECTOR) === null;

const clamp = (value: number, min: number, max: number): number =>
    // A viewport smaller than the strip that has to stay visible leaves no valid range.
    min > max ? 0 : Math.min(Math.max(value, min), max);

type ClampDragOffsetArgs = {
    offset: AxleDialogShellDragOffset;
    // The paper's position with no drag offset applied.
    paperRect: AxleDialogShellRect;
    viewport: { width: number; height: number };
    bounds: AxleDialogShellDragBounds;
    // In 'overhang' mode, how much of the dialog has to stay on screen.
    minVisible: number;
};

export const clampDragOffset = ({
    offset,
    paperRect,
    viewport,
    bounds,
    minVisible,
}: ClampDragOffsetArgs): AxleDialogShellDragOffset => {
    // 'contain' holds every edge inside the viewport; 'overhang' only holds a strip
    // of the dialog on screen, so the rest can move past an edge.
    const visible =
        bounds === 'contain'
            ? { x: paperRect.width, y: paperRect.height }
            : { x: minVisible, y: minVisible };
    return {
        x: clamp(
            offset.x,
            visible.x - paperRect.left - paperRect.width,
            viewport.width - visible.x - paperRect.left,
        ),
        // The top edge stays at or below the viewport top, so the header is always reachable.
        y: clamp(offset.y, -paperRect.top, viewport.height - visible.y - paperRect.top),
    };
};

export const toTranslateStyle = (offset: AxleDialogShellDragOffset): string | undefined =>
    offset.x === 0 && offset.y === 0 ? undefined : `${offset.x}px ${offset.y}px`;

/* The element's layout box, relative to the viewport-sized modal root.
 *
 * Read from offsets rather than `getBoundingClientRect`, because the transition's
 * transform sits on the dialog container — an ancestor — so a measured rect is
 * displaced by however far through the animation the dialog is. Layout offsets ignore
 * transforms, and the drag offset does not change them.
 */
export const getLayoutRect = (element: HTMLElement): AxleDialogShellRect => ({
    top: element.offsetTop,
    left: element.offsetLeft,
    width: element.offsetWidth,
    height: element.offsetHeight,
});
